import { useCrossProtocol } from '@/contexts/CrossProtocolContext/CrossProtocolContext'
import { useProtocol } from '@/hooks/useProtocol'
import type { ConnectedAccount } from '@/types/account'
import { strings } from '@/strings'

interface Props {
    onClose: () => void
}

export function CrossProtocolSettings({ onClose }: Props) {
    const { enabledAccountIds, setEnabledAccountIds } = useCrossProtocol()
    const { connectedAccounts } = useProtocol()

    const blueskyAccounts = connectedAccounts.filter(a => a.platform === 'bluesky')

    const isEnabled = (account: ConnectedAccount) => {
        if (account.platform === 'bluesky') {
            // Single Bluesky account: always on. Multiple: radio — must be explicitly selected.
            return blueskyAccounts.length <= 1 || enabledAccountIds.includes(account.id)
        }
        // Fediverse: no platform-specific filter → all enabled; otherwise check this account
        const fediverseAccountIds = connectedAccounts
            .filter(a => a.platform !== 'bluesky')
            .map(a => a.id)
        const enabledFediverseIds = enabledAccountIds.filter(id => fediverseAccountIds.includes(id))
        return enabledFediverseIds.length === 0 || enabledAccountIds.includes(account.id)
    }

    const toggleAccount = (account: ConnectedAccount) => {
        if (account.platform === 'bluesky' && blueskyAccounts.length <= 1) return

        if (isEnabled(account)) {
            if (account.platform !== 'bluesky') {
                const fediverseIds = connectedAccounts
                    .filter(a => a.platform !== 'bluesky')
                    .map(a => a.id)
                const enabledFediverseIds = enabledAccountIds.filter(id =>
                    fediverseIds.includes(id)
                )
                if (enabledFediverseIds.length === 0) {
                    // "All fediverse enabled" implicit state — expand to explicit list, remove this one
                    setEnabledAccountIds([
                        ...enabledAccountIds.filter(id => !fediverseIds.includes(id)),
                        ...fediverseIds.filter(id => id !== account.id),
                    ])
                    return
                }
            }
            setEnabledAccountIds(enabledAccountIds.filter(id => id !== account.id))
        } else {
            // Radio: remove other accounts of same protocol, add this one
            const otherSameProtocolIds = connectedAccounts
                .filter(a => a.platform === account.platform && a.id !== account.id)
                .map(a => a.id)
            setEnabledAccountIds([
                ...enabledAccountIds.filter(id => !otherSameProtocolIds.includes(id)),
                account.id,
            ])
        }
    }

    return (
        <>
            <div
                data-testid="settings-backdrop"
                className="fixed inset-0 bg-black/50 z-40"
                onClick={onClose}
            />
            <div className="fixed bottom-0 left-0 right-0 z-50 bg-surface-light dark:bg-surface-dark rounded-t-2xl p-4 pb-8 border-t border-border-light dark:border-border-dark">
                <div className="w-8 h-1 bg-border-light dark:bg-border-dark rounded-full mx-auto mb-4" />
                <h2 className="text-sm font-semibold uppercase tracking-wide text-text-secondary-light dark:text-text-secondary-dark mb-1">
                    {strings.crossProtocol.accountsInFeed}
                </h2>
                <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-3 opacity-70">
                    Select one account per protocol. Posts from each will appear together in your
                    Social Web feed.
                </p>
                <ul className="space-y-3">
                    {connectedAccounts.map(account => (
                        <li key={account.id} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                {account.avatar ? (
                                    <img
                                        src={account.avatar}
                                        className="w-7 h-7 rounded-full"
                                        alt=""
                                    />
                                ) : (
                                    <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-xs text-white font-semibold">
                                        {account.displayName[0]}
                                    </div>
                                )}
                                <div>
                                    <p className="text-sm font-medium">{account.handle}</p>
                                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                                        {account.platform === 'bluesky'
                                            ? 'Atmosphere'
                                            : account.platform.charAt(0).toUpperCase() +
                                              account.platform.slice(1)}
                                    </p>
                                </div>
                            </div>
                            <input
                                type="checkbox"
                                role="checkbox"
                                aria-label={account.handle}
                                checked={isEnabled(account)}
                                disabled={
                                    account.platform === 'bluesky' && blueskyAccounts.length <= 1
                                }
                                onChange={() => toggleAccount(account)}
                                className="accent-primary w-4 h-4"
                            />
                        </li>
                    ))}
                </ul>
            </div>
        </>
    )
}
